import { useEffect, useState } from 'react';
import { Alert, Button, Card, CardContent, Collapse, LinearProgress } from '@mui/material';
import { Stack, Typography } from '../components/mui-system';
import { EditorOnly } from '../components/EditorOnly';
import { api } from '../api/client';
import type { DonorImportJob } from '../../../api/src/donors/donors.types';

const labels: Record<DonorImportJob['status'], string> = {
  queued: 'В очереди',
  running: 'Импортируется',
  retrying: 'Ожидает повторной попытки',
  completed: 'Завершён',
  failed: 'Остановлен с ошибкой',
};
export function DonorImportJobs({ workspace, revision }: { workspace: string; revision: number }) {
  const [jobs, setJobs] = useState<DonorImportJob[]>([]);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [resuming, setResuming] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  useEffect(() => {
    let current = true;
    let delay = 2000;
    let timer: ReturnType<typeof setTimeout>;
    async function refresh() {
      try {
        const result = await api.donorImports(workspace);
        if (current) {
          setJobs(result);
          setError('');
          delay = result.some((job) => ['queued', 'running', 'retrying'].includes(job.status))
            ? 2000
            : 30_000;
        }
      } catch (caught) {
        if (current)
          setError(
            `Не удалось обновить прогресс импорта: ${caught instanceof Error ? caught.message : String(caught)}. Показаны последние полученные данные.`,
          );
      } finally {
        if (current) timer = setTimeout(() => void refresh(), delay);
      }
    }
    void refresh();
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [workspace, revision, reload]);
  async function resume(id: string) {
    setResuming(id);
    setActionError('');
    try {
      await api.resumeDonorImport(workspace, id);
      setReload((value) => value + 1);
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setResuming(null);
    }
  }
  if (!jobs.length && !error) return null;
  const unfinished = jobs.filter((job) => job.status !== 'completed');
  const completed = jobs.filter((job) => job.status === 'completed');
  const active = unfinished.some((job) => job.status !== 'failed');
  const details = (job: DonorImportJob) => (
    <Stack key={job.id} gap={1}>
      <Typography sx={{ overflowWrap: 'anywhere' }}>
        {job.fileName} · {labels[job.status]}
      </Typography>
      {job.status !== 'completed' && (
        <LinearProgress
          aria-label={`Прогресс импорта ${job.fileName}`}
          variant="determinate"
          value={job.total ? (job.processed / job.total) * 100 : 0}
        />
      )}
      <Typography>
        Обработано статей: {job.processed} из {job.total}; добавлено: {job.added}; обновлено:{' '}
        {job.updated}; без изменений: {job.unchanged}; архивных пропущено: {job.archivedSkipped}.
      </Typography>
      <Typography variant="caption">
        Дублей строк: {job.duplicates}; строк объединено: {job.mergedRows}; пустых: {job.empty}.{' '}
        {job.status === 'completed' ? 'Завершено' : 'Сохранено'}:{' '}
        {new Date(job.updatedAt).toLocaleString('ru-RU')}.
      </Typography>
      {job.error && (
        <Alert severity={job.status === 'failed' ? 'error' : 'warning'}>{job.error}</Alert>
      )}
      {job.status === 'failed' && (
        <EditorOnly permissions="donors.import">
          <Button disabled={resuming !== null} onClick={() => void resume(job.id)}>
            Продолжить импорт
          </Button>
        </EditorOnly>
      )}
    </Stack>
  );
  return (
    <Stack gap={1.5} mb={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {actionError && <Alert severity="error">{actionError}</Alert>}
      {unfinished.length > 0 && (
        <Card>
          <CardContent>
            <Stack gap={2}>
              <Typography variant="h6">Импорт CSV</Typography>
              <Typography variant="body2">
                {active
                  ? 'Импорт продолжается в фоне. Прогресс сохраняется после каждой пачки; страницу можно закрыть.'
                  : 'Импорт остановлен. Устраните причину ошибки и продолжите с сохранённого места.'}
              </Typography>
              {unfinished.map(details)}
            </Stack>
          </CardContent>
        </Card>
      )}
      {completed.length > 0 && (
        <>
          <Button
            size="small"
            sx={{ alignSelf: 'flex-start' }}
            aria-expanded={historyOpen}
            aria-controls="donor-import-history"
            onClick={() => setHistoryOpen((open) => !open)}
          >
            История импортов ({completed.length})
          </Button>
          <Collapse in={historyOpen} unmountOnExit>
            <Card id="donor-import-history">
              <CardContent>
                <Stack gap={2}>
                  <Typography variant="h6">Завершённые импорты</Typography>
                  {completed.map(details)}
                </Stack>
              </CardContent>
            </Card>
          </Collapse>
        </>
      )}
    </Stack>
  );
}
